// SVG icons + small design primitives. Web-only — uses inline `<svg>`,
// `<div>`, etc. apps/mobile will get its own file using react-native-svg
// (the platform-split is intentional; see Approach B in the migration
// plan in MEMORY).
//
// Ported from the legacy `icons.jsx`. Names + paths preserved verbatim
// so existing screens don't have to relabel.

import type { CSSProperties } from 'react';
import { initialsOf } from '@nayto/i18n';

// ─── Icon ────────────────────────────────────────────────────────────

type IconName =
  | 'home'
  | 'live'
  | 'leaderboard'
  | 'ideas'
  | 'tools'
  | 'settings'
  | 'search'
  | 'bell'
  | 'filter'
  | 'grid'
  | 'chevronRight'
  | 'chevronLeft'
  | 'chevronDown'
  | 'chevronUp'
  | 'arrowUp'
  | 'arrowDown'
  | 'arrowRight'
  | 'plus'
  | 'close'
  | 'check'
  | 'heart'
  | 'message'
  | 'share'
  | 'user'
  | 'users'
  | 'sparkles'
  | 'target'
  | 'trash'
  | 'starOutline'
  | 'starFill'
  | 'flag'
  | 'trendUp'
  | 'trendDown'
  | 'eye'
  | 'play'
  | 'film'
  | 'image'
  | 'text'
  | 'mic'
  | 'send'
  | 'translate'
  | 'caption'
  | 'facebook'
  | 'twitter'
  | 'instagram'
  | 'youtube'
  | 'tiktok'
  | 'telegram'
  | 'phone'
  | 'circle'
  | 'radio'
  | 'alert'
  | 'info'
  | 'tag'
  | 'mapPin'
  | 'calendar'
  | 'globe'
  | 'admin'
  | 'clipboardCheck'
  | 'refresh'
  | 'activity'
  | 'copy'
  | 'externalLink'
  | 'printer';

const ICON_PATHS: Record<IconName, string> = {
  home: 'M3 9l9-7 9 7v11a2 2 0 01-2 2h-4v-6h-6v6H5a2 2 0 01-2-2z',
  live: 'M4 4h16v12H5.17L4 17.17V4z M4 22l3-3h13a1 1 0 001-1V8',
  leaderboard: 'M4 20h4v-8H4v8zm6 0h4V4h-4v16zm6 0h4v-12h-4v12z',
  ideas: 'M9 21h6m-6-4h6m-8-5a6 6 0 1112 0c0 1.8-.8 3.4-2 4.5V17H9v-.5C7.8 15.4 7 13.8 7 12z',
  tools:
    'M14.7 6.3a4 4 0 11-5.4 5.4l-6.6 6.6a2.1 2.1 0 003 3l6.6-6.6a4 4 0 005.4-5.4l-3 3-2.4-2.4 3-3z',
  settings:
    'M12 15a3 3 0 100-6 3 3 0 000 6z M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h0a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51h0a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v0a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z',
  search: 'M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.35-4.35',
  bell: 'M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 01-3.46 0',
  filter: 'M3 6h18M6 12h12M10 18h4',
  grid: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
  chevronRight: 'M9 18l6-6-6-6',
  chevronLeft: 'M15 18l-6-6 6-6',
  chevronDown: 'M6 9l6 6 6-6',
  chevronUp: 'M18 15l-6-6-6 6',
  arrowUp: 'M12 19V5M5 12l7-7 7 7',
  arrowDown: 'M12 5v14M19 12l-7 7-7-7',
  arrowRight: 'M5 12h14M12 5l7 7-7 7',
  plus: 'M12 5v14M5 12h14',
  close: 'M18 6L6 18M6 6l12 12',
  check: 'M20 6L9 17l-5-5',
  heart:
    'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z',
  message:
    'M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z',
  share:
    'M18 8a3 3 0 100-6 3 3 0 000 6zM6 15a3 3 0 100-6 3 3 0 000 6zM18 22a3 3 0 100-6 3 3 0 000 6zM8.59 13.51l6.83 3.98M15.41 6.51L8.59 10.49',
  user: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z',
  users:
    'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
  sparkles:
    'M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3zM19 14l.75 2.25L22 17l-2.25.75L19 20l-.75-2.25L16 17l2.25-.75L19 14zM5 14l.75 2.25L8 17l-2.25.75L5 20l-.75-2.25L2 17l2.25-.75L5 14z',
  target: 'M12 22a10 10 0 100-20 10 10 0 000 20zM12 18a6 6 0 100-12 6 6 0 000 12zM12 14a2 2 0 100-4 2 2 0 000 4z',
  trash:
    'M3 6h18 M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2 M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6 M10 11v6 M14 11v6',
  starOutline: 'M12 2l2.9 6.88L22 9.27l-5.4 4.73L18.18 21 12 17.27 5.82 21l1.58-6.99L2 9.27l7.1-.39L12 2z',
  starFill: 'M12 2l2.9 6.88L22 9.27l-5.4 4.73L18.18 21 12 17.27 5.82 21l1.58-6.99L2 9.27l7.1-.39L12 2z',
  flag: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7',
  trendUp: 'M23 6l-9.5 9.5-5-5L1 18M17 6h6v6',
  trendDown: 'M23 18l-9.5-9.5-5 5L1 6M17 18h6v-6',
  eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 15a3 3 0 100-6 3 3 0 000 6z',
  play: 'M5 3l14 9-14 9V3z',
  film: 'M3 4h18v16H3V4zM3 8h18M3 16h18M7 4v16M17 4v16',
  image: 'M3 5h18v14H3V5zM3 15l5-5 4 4 3-3 6 6M8 10a1.5 1.5 0 100-3 1.5 1.5 0 000 3z',
  text: 'M4 6h16M4 12h16M4 18h10',
  mic: 'M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM19 10v2a7 7 0 11-14 0v-2M12 19v4M8 23h8',
  send: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
  translate: 'M5 8l6 6M4 14l6-6 2-3M2 5h12M7 2h1M22 22l-5-10-5 10M14 18h6',
  caption: 'M2 3h20v14H2V3zM6 8h2m0 0v4M12 8h2m0 0v4M18 8h-2m0 0v4',
  facebook: 'M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3V2z',
  twitter:
    'M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z',
  instagram:
    'M17 2H7a5 5 0 00-5 5v10a5 5 0 005 5h10a5 5 0 005-5V7a5 5 0 00-5-5zM16 11.37a4 4 0 11-7.914 1.173 4 4 0 017.914-1.173zM17.5 6.5h.01',
  youtube:
    'M22.54 6.42a2.78 2.78 0 00-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 00-1.94 2A29 29 0 001 11.75a29 29 0 00.46 5.33A2.78 2.78 0 003.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 001.94-2 29 29 0 00.46-5.25 29 29 0 00-.46-5.33z M9.75 15.02l5.75-3.27-5.75-3.27v6.54z',
  tiktok: 'M9 12a4 4 0 104 4V4a5 5 0 005 5',
  telegram: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
  phone:
    'M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z',
  circle: 'M12 22a10 10 0 100-20 10 10 0 000 20z',
  radio:
    'M4.9 19.1C1 15.2 1 8.8 4.9 4.9M7.8 16.2a5 5 0 010-7M19.1 4.9a8.95 8.95 0 010 14.2M16.2 7.8a5 5 0 010 7M12 14a2 2 0 100-4 2 2 0 000 4z',
  alert: 'M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z',
  info: 'M12 22a10 10 0 100-20 10 10 0 000 20zM12 16v-4M12 8h.01',
  tag: 'M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z M7 7h.01',
  mapPin: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0zM12 13a3 3 0 100-6 3 3 0 000 6z',
  calendar: 'M19 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2zM16 2v4M8 2v4M3 10h18',
  globe: 'M12 22a10 10 0 100-20 10 10 0 000 20zM2 12h20M12 2a15 15 0 010 20a15 15 0 010-20',
  admin: 'M12 2l9 4v6c0 5-3.8 9.3-9 10-5.2-.7-9-5-9-10V6l9-4z M9 12l2 2 4-4',
  clipboardCheck:
    'M9 4h6v3H9z M9 5.5H7a1 1 0 00-1 1V20a1 1 0 001 1h10a1 1 0 001-1V6.5a1 1 0 00-1-1h-2 M9 13.5l2 2 4-4',
  refresh: 'M23 4v6h-6 M1 20v-6h6 M3.51 9a9 9 0 0114.85-3.36L23 10 M20.49 15a9 9 0 01-14.85 3.36L1 14',
  activity: 'M22 12h-4l-3 9L9 3l-3 9H2',
  copy: 'M20 9h-9a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-9a2 2 0 00-2-2zM5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1',
  externalLink: 'M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6 M15 3h6v6 M10 14L21 3',
  printer: 'M6 9V2h12v7 M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2 M6 14h12v8H6z',
};

interface IconProps {
  name: IconName | (string & {}); // tolerate stringly-typed callers during migration
  size?: number;
  color?: string;
  strokeWidth?: number;
  fill?: string;
}

export function Icon({
  name,
  size = 20,
  color = 'currentColor',
  strokeWidth = 2,
  fill = 'none',
}: IconProps) {
  const path = ICON_PATHS[name as IconName] ?? ICON_PATHS.circle;
  // `starFill` aliases starOutline but defaults to filled with the stroke
  // color. Callers can still pass an explicit `fill` to override.
  const effectiveFill = fill !== 'none' ? fill : name === 'starFill' ? color : 'none';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={effectiveFill}
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d={path} />
    </svg>
  );
}

// ─── PlatformBadge ───────────────────────────────────────────────────

type Platform = 'facebook' | 'twitter' | 'instagram' | 'youtube' | 'tiktok' | 'telegram';

const PLATFORM_COLORS: Record<Platform, string> = {
  facebook: '#1877f2',
  twitter: '#000',
  instagram: '#e4405f',
  youtube: '#ff0000',
  tiktok: '#000',
  telegram: '#0088cc',
};

interface PlatformBadgeProps {
  platform: Platform | (string & {});
  size?: number;
}

export function PlatformBadge({ platform, size = 16 }: PlatformBadgeProps) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size / 4,
        background: PLATFORM_COLORS[platform as Platform] ?? '#666',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        flexShrink: 0,
      }}
    >
      <Icon name={platform} size={size * 0.6} strokeWidth={2.4} />
    </div>
  );
}

// ─── Avatar ──────────────────────────────────────────────────────────

interface AvatarProps {
  name: string | null | undefined;
  color?: string;
  size?: number;
}

// Avatar — circular initials chip, falls back to a person icon when the
// name doesn't yield real initials (phone numbers, empty, etc). The
// fallback is what makes this safe to feed `auth.user.phoneNumber`
// directly when no displayName is available from the server yet.
export function Avatar({ name, color, size = 36 }: AvatarProps) {
  const initials = initialsOf(name);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: color ?? '#666',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: size * 0.4,
        letterSpacing: -0.2,
        flexShrink: 0,
      }}
    >
      {initials ?? <Icon name="user" size={size * 0.55} color="#fff" strokeWidth={1.8} />}
    </div>
  );
}

// ─── signalColor ─────────────────────────────────────────────────────

type SignalKind = 'critical' | 'warning' | 'positive' | 'insight' | 'contextual';

const SIGNAL_COLORS_LIGHT: Record<SignalKind, string> = {
  critical: '#dc2626',
  warning: '#d97706',
  positive: '#16a34a',
  insight: '#7c3aed',
  contextual: '#475569',
};
const SIGNAL_COLORS_DARK: Record<SignalKind, string> = {
  critical: '#ef4444',
  warning: '#f59e0b',
  positive: '#22c55e',
  insight: '#8b5cf6',
  contextual: '#64748b',
};

export function signalColor(kind: SignalKind | string, theme: 'light' | 'dark'): string {
  const map = theme === 'dark' ? SIGNAL_COLORS_DARK : SIGNAL_COLORS_LIGHT;
  return map[kind as SignalKind] ?? '#666';
}

// ─── Sparkline ───────────────────────────────────────────────────────

interface SparklineProps {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
}

export function Sparkline({ data, color = 'currentColor', width = 60, height = 20 }: SparklineProps) {
  if (data.length === 0) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((v - min) / range) * height;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg width={width} height={height} style={{ overflow: 'visible', flexShrink: 0 }}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── HBar ────────────────────────────────────────────────────────────

interface HBarProps {
  value: number;
  max?: number;
  color: string;
  trackColor?: string;
  height?: number;
  style?: CSSProperties;
}

export function HBar({
  value,
  max = 100,
  color,
  trackColor = 'rgba(0,0,0,0.06)',
  height = 4,
  style,
}: HBarProps) {
  return (
    <div
      style={{
        flex: 1,
        height,
        background: trackColor,
        borderRadius: height,
        overflow: 'hidden',
        ...style,
      }}
    >
      <div
        style={{
          width: `${(value / max) * 100}%`,
          height: '100%',
          background: color,
          borderRadius: height,
          transition: 'width 0.4s ease',
        }}
      />
    </div>
  );
}

export type { IconName, Platform, SignalKind };
